import { Button, InputArea, Notification } from '@wix/design-system'
import { useContent, useMetadata, useNext, usePageLoading } from '@wix/funnel-builder-lizard'
import React, { ChangeEventHandler, FC, KeyboardEventHandler, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { ambassadorHttpClient, CancelTokenSource } from '../../../../api/httpClient'
import { usePageLogger } from '../../../../bi/mfi'
import { useStorageContext } from '../../../../context/StorageContext/StorageContext'
import { useExperiments } from '../../../../hooks/useExperiments'
import { debounce } from '../../../../utils/debounce'
import { ContentWithLoading } from '../../../reusable/Button'
import { StateCategory } from '../../../reusable/Category/state/types'
import { WixMobileBackButton } from '../../../reusable/WixMobileBackButton/WixMobileBackButton'
import { WixMobileContentWrapper } from '../../../reusable/WixMobileContentWrapper/WixMobileContentWrapper'
import { WixMobileDescription } from '../../../reusable/WixMobileDescription/WixMobileDescription'
import { WixMobileFooter } from '../../../reusable/WixMobileFooter/WixMobileFooter'
import { WixMobileHeader } from '../../../reusable/WixMobileHeader/WixMobileHeader'
import { WixMobileHeading } from '../../../reusable/WixMobileHeading/WixMobileHeading'
import { WixMobilePage } from '../../../reusable/WixMobilePage/WixMobilePage'
import { WixMobileSkipButton } from '../../../reusable/WixMobileSkipButton/WixMobileSkipButton'
import { WixMobileStepProgressBar } from '../../../reusable/WixMobileStepProgressBar/WixMobileStepProgressBar'
import { goNext } from '../state/actions/goNext'
import { TWixMobileCategory } from '../WixMobileCategory.content'
import s from '../WixMobileCategory.scss'
import { WixMobileCategorySuggestions } from '../WixMobileCategorySuggestions/WixMobileCategorySuggestions'
import { MAX_QUERY_LENGTH } from './constants'
import { extractCategory as _extractCategory } from './extractCategory'
import { loadCategory } from './loadCategory'

// this can be bigger since typing on mobile is harder than on desktop
const extractCategory = debounce(_extractCategory, 500)

export const WixMobileCategoryNoSuggestions: FC = () => {
  const { save, category: savedCategory } = useStorageContext()
  const pageLogger = usePageLogger()
  const [pageLoading] = usePageLoading()
  const { content } = useContent<TWixMobileCategory>()
  const [{ language }] = useMetadata()
  const { questionKey } = content.question
  const next = useNext()
  const inputRef = useRef<InputArea>()
  const experiments = useExperiments()
  const [searchParams] = useSearchParams()
  const prefilledCategory = (searchParams.get(questionKey) || '').slice(0, MAX_QUERY_LENGTH)

  const isMounted = useRef(true)

  useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    void pageLogger.screenView({ initialData: prefilledCategory })
  }, [pageLogger])

  const cancel = useRef<CancelTokenSource | undefined>()

  const [requestError, setRequestError] = useState<Error | undefined>(undefined)

  useEffect(() => {
    const handle = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLTextAreaElement) {
        if (e.key === 'Escape') {
          e.target.blur()
        }
        return
      }
      if (e.key === '/') {
        e.preventDefault()
        inputRef.current?.focus()
        return
      }
    }

    document.addEventListener('keydown', handle)
    return () => {
      document.removeEventListener('keydown', handle)
    }
  }, [])

  useEffect(() => {
    inputRef.current?.textArea?.setAttribute('enterkeyhint', 'next')
  }, [])

  const [category, setCategory] = useState<StateCategory | undefined>(
    savedCategory ?? (prefilledCategory ? { search: prefilledCategory, searchSource: 'input', index: 0 } : undefined),
  )
  const [maxReached, setMaxReached] = useState((category?.search.trim().length || 0) >= MAX_QUERY_LENGTH)
  const [isLoading, setIsLoading] = useState(false)

  const handleBack = () => {
    if (pageLoading) {
      return
    }
    void pageLogger.back()
    window.history.back()
  }
  const handleSkip = () => {
    if (pageLoading) {
      return
    }
    void pageLogger.skip()
    next({ [questionKey]: { skipped: true } })
  }

  const handleNext = async (category: StateCategory) => {
    if (pageLoading) {
      return
    }

    goNext({
      next,
      save,
      category,
      searchValue: category?.search || '',
      questionKey,
      language,
      pageLogger,
      experiments,
      answerType: 'free text',
    })
    setIsLoading(false)
  }

  const doExtract = (value: string, onSuccess: (category: StateCategory) => void) => {
    setRequestError(undefined)
    cancel.current?.cancel()
    cancel.current = ambassadorHttpClient.CancelToken.source()
    const token = cancel.current.token
    extractCategory(value, language, token, (error, businessTerm, businessSegmentId) => {
      if (!isMounted.current) {
        return
      }
      if (error && ambassadorHttpClient.isCancel(error)) {
        return
      }
      setRequestError(error)
      onSuccess({ search: value, searchSource: 'select', index: 0, businessTerm, businessSegmentId })
    })
  }

  useEffect(() => {
    if (savedCategory || !prefilledCategory) {
      return
    }
    doExtract(prefilledCategory, setCategory)
  }, [])

  const doLoad = (label: string, index: number, onSuccess: (category: StateCategory) => void) => {
    setRequestError(undefined)
    cancel.current?.cancel()
    cancel.current = ambassadorHttpClient.CancelToken.source()
    const token = cancel.current.token
    // for suggestion click load without debounce
    loadCategory(label, language, token, (error, businessTerm, businessSegmentId) => {
      if (!isMounted.current) {
        return
      }
      if (error && ambassadorHttpClient.isCancel(error)) {
        return
      }
      setRequestError(error)
      onSuccess({ search: label, searchSource: 'suggestions', index: index + 1, businessTerm, businessSegmentId })
    })
  }

  const handleSearchChange: ChangeEventHandler<HTMLTextAreaElement> = e => {
    const value = e.target.value
    setCategory({ search: value, searchSource: 'input', index: 0 })
    setMaxReached(maxReached || value.length >= MAX_QUERY_LENGTH)
    const tValue = value.trim()
    if (!tValue || tValue === category?.search.trim()) {
      return
    }
    doExtract(value, setCategory)
  }

  const handleSearchEnter: KeyboardEventHandler<HTMLTextAreaElement> = e => {
    e.preventDefault()
    const input = e.target
    if (!(input instanceof HTMLTextAreaElement)) {
      return
    }
    input.blur()

    if (category?.businessTerm) {
      return handleNext(category)
    }

    setIsLoading(true)
    return doExtract(input.value, category => {
      setCategory(category)
      handleNext(category)
    })
  }

  const handleNextClick = () => {
    if (!category || isLoading) {
      return
    }
    if (category.businessTerm) {
      return handleNext(category)
    }

    setIsLoading(true)
    if (category.searchSource === 'select') {
      return doLoad(category.search, category.index, category => {
        setCategory(category)
        handleNext(category)
      })
    }
    return doExtract(category.search, category => {
      setCategory(category)
      handleNext(category)
    })
  }

  const handleSuggestionClick = (label: string, index: number) => {
    void pageLogger.clickAnswer({ questionKey, buttonName: label, suggestedOption: true, position: index + 1 })
    if (label === category?.search) {
      return
    }
    setCategory({ search: label, searchSource: 'suggestions', index: index + 1 })
    doLoad(label, index, setCategory)
  }

  return (
    <WixMobilePage>
      <WixMobileHeader
        skin="harmony"
        leftContent={<WixMobileBackButton onBack={handleBack} />}
        rightContent={(content.skip && <WixMobileSkipButton onSkip={handleSkip} label={content.skip} />) || undefined}
      />
      <Notification
        theme="error"
        show={!!requestError}
        type="local"
        autoHideTimeout={5000}
        onClose={() => setRequestError(undefined)}
      >
        <Notification.TextLabel>
          {content.errorMessage || requestError?.message || 'Something bad happened'}
        </Notification.TextLabel>
      </Notification>
      <WixMobileContentWrapper>
        {content.stepProgressBar.content.visible && (
          <WixMobileStepProgressBar
            current={content.stepProgressBar.content.current}
            total={content.stepProgressBar.content.total}
            skin="harmony"
          />
        )}
        <WixMobileHeading skin="harmony">{content.question.content.title}</WixMobileHeading>
        <WixMobileDescription skin="harmony">{content.question.content.subtitle}</WixMobileDescription>
        <InputArea
          placeholder={content.question.content.placeholder}
          resizable={false}
          value={category?.search}
          onChange={handleSearchChange}
          onEnterPressed={handleSearchEnter}
          autoGrow
          minRowsAutoGrow={1}
          maxRowsAutoGrow={8}
          maxLength={MAX_QUERY_LENGTH}
          ref={inputRef as any}
          hasCounter={maxReached}
          className={s.inputArea}
        />
        <WixMobileCategorySuggestions
          suggestions={content.question.content.suggestions.items}
          onSuggestionClick={handleSuggestionClick}
        />
      </WixMobileContentWrapper>
      {content.next && (
        <WixMobileFooter>
          <Button
            fullWidth
            onClick={handleNextClick}
            disabled={!category?.search}
            size="large"
            className={s.nextButton}
          >
            <ContentWithLoading isLoading={isLoading || pageLoading}>{content.next}</ContentWithLoading>
          </Button>
        </WixMobileFooter>
      )}
    </WixMobilePage>
  )
}
